import React, { useState } from 'react';
import { Link, generatePath, useLocation, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Breadcrumbs, Typography } from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';
import Bluebird from 'bluebird';
import {
  StyledRescuerBreadcrumb,
  StyledRescuerTextBreadcrumb,
} from '../../rescuerDashboard/rescuerDashboardMobile/Components/StyledRescuerBreadcrumb';
import routes from '../../../routes';
import useActiveSite from '../../../hooks/useActiveSite';
import ToDoAlertCard from './ToDoAlertCard';
import { receiveRemoveSiteTodo, removeSiteTodo } from '../../../actions/sites';
import snackbarHelper from '../../../helpers/snackbarHelper';
import OverlayLoader from '../../../components/OverlayLoader';

const ToDoReviewPickupScheduleBreadcrumbs = () => (
  <Box boxShadow={0}>
    <Breadcrumbs aria-label="breadcrumb">
      <StyledRescuerBreadcrumb
        component={Link}
        to={generatePath(routes.mobileDashboard)}
        label="Home"
        icon={<HomeIcon style={{ color: '#fff' }} fontSize="small" />}
      />
      <StyledRescuerTextBreadcrumb component={Link} to={routes.mobileToDo} label="To-do" />
      <StyledRescuerTextBreadcrumb component="a" href="#" label="Adjust Pickup Schedule" />
    </Breadcrumbs>
  </Box>
);

const ToDoReviewPickupSchedule = () => {
  const location = useLocation();
  const history = useHistory();
  const activeSite = useActiveSite();
  const dispatch = useDispatch();
  const [currentCardIndex, setCurrentCardIndex] = useState(location.state?.toDoCardIndex || 0);
  const [loading, setLoading] = useState();
  const sdTodosEntities = useSelector((state) => state.entities.sites.sdTodos);
  const sdTodos = sdTodosEntities.bySiteId[activeSite.id];
  const reviewPickupSchedules = sdTodos?.reviewPickupSchedules || [];

  const handleSkipClick = () => {
    setCurrentCardIndex(currentCardIndex + 1);
  };

  const handleRemoveClick = (todo, handleCardOut, animationTime) => {
    setLoading(true);
    Bluebird.try(() => dispatch(removeSiteTodo(activeSite.id, todo.entity_id, todo.entity_type, todo.type, false)))
      .then(() => {
        handleCardOut();
        setTimeout(() => {
          dispatch(receiveRemoveSiteTodo(activeSite.id, todo.entity_id, todo.type));
        }, animationTime);
      })
      .catch((err) => {
        snackbarHelper.error(err.message);
      })
      .finally(() => setLoading(false));
  };

  const handleReviewClick = (todo, handleCardOut, animationTime) => {
    if (!todo?.details?.donation_id) return;
    setLoading(true);
    Bluebird.try(() => dispatch(removeSiteTodo(activeSite.id, todo.entity_id, todo.entity_type, todo.type, false)))
      .then(() => {
        handleCardOut();
        setTimeout(() => {
          dispatch(receiveRemoveSiteTodo(activeSite.id, todo.entity_id, todo.type));
          history.push(generatePath(routes.donation_edit, { donationId: todo.details.donation_id }));
        }, animationTime);
      })
      .catch((err) => {
        snackbarHelper.error(err.message);
      })
      .finally(() => setLoading(false));
  };

  if (!reviewPickupSchedules.length || currentCardIndex + 1 > reviewPickupSchedules.length) {
    return (
      <>
        <ToDoReviewPickupScheduleBreadcrumbs />
        <Box
          sx={{
            width: '100%',
            height: 'fit-content',
            backgroundColor: 'background.default',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            paddingLeft: 2,
            paddingRight: 2,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              '-webkit-transform': 'translate(-50%, -50%)',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            <Typography color="textSecondary" variant="h5">
              You're all caught up, great work!
            </Typography>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <OverlayLoader loading={loading}>
      <ToDoReviewPickupScheduleBreadcrumbs />

      <Box
        sx={{
          width: '100%',
          height: 'fit-content',
          backgroundColor: 'background.default',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          paddingLeft: 2,
          paddingRight: 2,
        }}
      >
        <Box sx={{ position: 'relative', height: '100%' }}>
          {reviewPickupSchedules.map((item, index) => (
            <ToDoAlertCard
              key={item.entity_id}
              rescue={item}
              current={index === currentCardIndex}
              next={reviewPickupSchedules.length <= index ? null : index === currentCardIndex + 1}
              onRemoveClick={handleRemoveClick}
              onSkipClick={handleSkipClick}
              onReviewClick={handleReviewClick}
              reviewLabel="Review"
              content={
                <span>
                  <b>{item?.details?.donor_location_name}</b> has had no food available in{' '}
                  <b>{item?.details?.no_food_count} of the last 6</b> rescues, in their{' '}
                  <b>{item?.details?.frequency_name}</b>{' '}
                  delivery to <b>{item?.details?.receiver_location_name}</b>.
                </span>
              }
            />
          ))}
        </Box>
      </Box>
    </OverlayLoader>
  );
};

export default ToDoReviewPickupSchedule;
